/**
 * WS-клиент: снапшот + дельты, переподключение, контроль версии протокола.
 *
 * Две вещи, из-за которых это написано отдельным модулем:
 *
 * 1. Вкладка сканера живёт у пользователя неделями. Если сервер поменяет формат
 *    дельт, старый клиент начнёт молча показывать неверные числа. Поэтому
 *    снапшот несёт `proto`, и при несовпадении клиент перезагружает страницу
 *    сам, а не пытается разобрать чужой формат.
 * 2. Разрыв WS в момент живой вилки — потерянные деньги. Реконнект с backoff и
 *    повторным запросом снапшота обязателен, а не «потом добавим».
 */

import { alignFork } from '../lib/align';
import { isMainFeed2Id } from '../lib/feedContext';
import type { Fork } from '../lib/protocol';

export type { Fork };

interface Snapshot {
  t: 's';
  proto: number;
  seq: number;
  forks: Fork[];
  m?: Record<string, unknown>;
}

/**
 * Ушедшая вилка. Первый протокол называет только идентификатор, второй — ещё и
 * причину.
 *
 * Разбирать надо оба вида, и не ради вежливости к старому серверу. Приняв объект
 * за строку, `Map.delete` промахнётся молча: вилка останется в ленте навсегда,
 * с тикающим возрастом и ценой, которой на площадке уже нет.
 */
type Gone = string | { id: string; why?: string };

const goneId = (gone: Gone): string => (typeof gone === 'string' ? gone : gone.id);

interface Delta {
  t: 'd';
  seq: number;
  a?: Fork[];
  u?: Partial<Fork>[];
  r?: Gone[];
  m?: Record<string, unknown>;
}

interface Heartbeat {
  t: 'hb';
  seq: number;
}

/**
 * Сервер уходит и называет срок возврата. Кадр появился вместе с выкатом второго
 * контура: он не помещается в память дважды, blue-green там не будет, и выкат —
 * это остановка процесса со всеми сокетами разом.
 */
interface Bye {
  t: 'bye';
  why?: string;
  backInSeconds?: number;
}

interface SeatRevoked {
  t: 'seat_revoked';
  why?: string;
}

type Message = Snapshot | Delta | Heartbeat | Bye | SeatRevoked;

/**
 * `paused` — это не разрыв, а объявленная сервером пауза. Отдельно от
 * `reconnecting` потому, что человеку это разные новости: «связь потеряна,
 * причина неизвестна» пугает и заставляет перезагружать вкладку, «обновляемся,
 * вернусь через N секунд» — нет.
 */
export type FeedStatus = 'connecting' | 'live' | 'reconnecting' | 'paused' | 'revoked';

/** Сколько ждать после объявленной паузы сверх обещанного срока, мс. */
const BACK_EARLY_MS = 500;
/**
 * Разброс на переподключение. Без него сотня вкладок стучится в один и тот же
 * миллисекундный слот и добивает процесс ровно в момент его подъёма — то есть
 * прощальный кадр превращает разрыв у всех сразу в залп у всех сразу.
 */
const jitter = (delay: number): number => delay * (0.75 + Math.random() * 0.5);

export interface ClientCallbacks {
  onState: (forks: Map<string, Fork>, meta: Record<string, unknown>) => void;
  onStatus: (status: FeedStatus) => void;
  /** Сервер ушёл на другую версию протокола — единственный верный ответ перезагрузиться. */
  onProtocolMismatch: (serverProto: number) => void;
  /**
   * Вилка ушла и сервер назвал причину. Необязателен: экран, который причины не
   * показывает, просто не подписывается.
   */
  onGone?: (id: string, why: string) => void;
  /** Место забрал другой экран — переподключаться нельзя: это петля на 30 с. */
  onSeatRevoked?: (why: string) => void;
}

/**
 * Возраст тикает локально от первого кадра. Повторный снапшот не имеет права
 * ставить `at=сейчас`: иначе `ageOf` = server.age + 0, секунды прыгают назад
 * и «живёт N с» залипает, пока сервер не пришлёт новый age.
 */
const stamp = (fork: Fork, now: number, prev?: Fork): Fork => ({
  ...fork,
  age: prev?.age ?? fork.age,
  at: prev?.at ?? now,
});

export class FeedClient {
  private ws: WebSocket | null = null;
  private forks = new Map<string, Fork>();
  private meta: Record<string, unknown> = {};
  private seq = 0;
  private attempt = 0;
  private closed = false;
  /** Обещанный сервером срок возврата, мс. Ноль — прощания не было. */
  private backIn = 0;
  /** Возвращаемся после объявленной паузы: пугать словом «связь» ещё рано. */
  private resuming = false;
  /**
   * Вилки «в работе». Снапшот режет по ROI и байтам — afterlife (минус)
   * выпадает первым. Без удержания патч приходит в пустоту: id уже нет в
   * `forks`, карточка замирает на призраке. Это не новый сокет и не poll.
   */
  private watched = new Set<string>();

  constructor(
    private readonly url: string,
    private readonly expectedProto: number,
    private readonly cb: ClientCallbacks,
  ) {}

  /** Держать эти id в кадре, даже если снапшот их обрезал. */
  watch(ids: readonly string[]): void {
    this.watched = new Set(ids);
  }

  connect(): void {
    this.closed = false;
    if (this.resuming) this.cb.onStatus('paused');
    else this.cb.onStatus(this.attempt === 0 ? 'connecting' : 'reconnecting');
    const ws = new WebSocket(this.url);
    this.ws = ws;

    ws.onopen = () => {
      this.attempt = 0;
      this.backIn = 0;
      this.resuming = false;
      this.cb.onStatus('live');
    };

    ws.onmessage = (ev) => {
      const now = Date.now();
      const msg = JSON.parse(ev.data as string) as Message;
      if (msg.t === 'seat_revoked') {
        this.closed = true;
        this.cb.onSeatRevoked?.(typeof msg.why === 'string' && msg.why !== '' ? msg.why : 'seat_taken');
        this.cb.onStatus('revoked');
        ws.close();
        return;
      }
      if (msg.t === 's') {
        if (msg.proto !== this.expectedProto) {
          this.close();
          this.cb.onProtocolMismatch(msg.proto);
          return;
        }
        const next = new Map(
          msg.forks.map((f) => [f.id, stamp(alignFork(f), now, this.forks.get(f.id))]),
        );
        // Обрезанный кадр не имеет права забрать работу: копируем то, что уже
        // видели. Настоящая смерть приходит `r`, не отсутствием в снапшоте.
        for (const id of this.watched) {
          const keep = this.forks.get(id);
          // Main's bridge publishes the complete feed2 snapshot (no r deltas).
          // Absence is death, including a held calculator: the screen retains
          // its last values as a ghost instead of claiming the quote is live.
          if (isMainFeed2Id(id)) continue;
          if (keep !== undefined && !next.has(id)) next.set(id, keep);
        }
        this.forks = next;
        this.seq = msg.seq;
        this.meta = msg.m ?? {};
        this.cb.onState(this.forks, { ...this.meta, proto: msg.proto, snap: true });
        return;
      }
      if (msg.t === 'hb') return;
      if (msg.t === 'bye') {
        // Сокет ещё открыт: сервер успел сказать раньше, чем закрыть. Считаем
        // это паузой, а не разрывом, и ждём ровно обещанный срок — свой отступ
        // здесь не нужен, о сроке нам сказали.
        this.backIn = Math.max(0, (msg.backInSeconds ?? 0) * 1000);
        this.attempt = 0;
        this.cb.onStatus('paused');
        return;
      }

      // Пропущенная дельта означает расхождение состояния. Тихо жить с этим
      // нельзя: переподключаемся и берём снапшот заново.
      if (msg.seq <= this.seq) return;
      if (msg.seq > this.seq + 1) {
        this.reconnect();
        return;
      }
      this.seq = msg.seq;
      for (const fork of msg.a ?? []) {
        this.forks.set(fork.id, stamp(alignFork(fork), now, this.forks.get(fork.id)));
      }
      for (const patch of msg.u ?? []) {
        const id = patch.id as string;
        const current = this.forks.get(id);
        // Отметку времени обновляем только вместе с возрастом: патч без него не
        // означает, что вилка помолодела.
        if (current) {
          // Сначала слить, потом свести `o` и ноги: иначе новый кэф сверху и
          // старые шеры в ногах — две правды на одной карточке.
          const merged = alignFork({ ...current, ...patch });
          this.forks.set(id, patch.age === undefined ? merged : stamp(merged, now, current));
        } else if (this.watched.has(id) && patch.id !== undefined) {
          // Снапшот успел выкинуть id до watch: патч без базы не сведём,
          // но полный объект в `u` (редко) всё же примем.
          const raw = patch as Fork;
          if (Array.isArray(raw.legs) && raw.legs.length >= 2) {
            this.forks.set(id, stamp(alignFork(raw), now, current));
          }
        }
      }
      for (const gone of msg.r ?? []) {
        const id = goneId(gone);
        this.forks.delete(id);
        if (typeof gone !== 'string') this.cb.onGone?.(id, gone.why ?? 'other');
      }
      this.meta = msg.m ?? this.meta;
      this.cb.onState(this.forks, this.meta);
    };

    ws.onclose = () => {
      if (!this.closed) this.reconnect();
    };
    ws.onerror = () => ws.close();
  }

  private reconnect(): void {
    this.ws?.close();
    this.ws = null;
    // Объявленная пауза не считается неудачной попыткой: сервер не упал, он
    // предупредил. Забыть об этом — значит нарастить отступ на ровном месте и
    // вернуться через полминуты после того, как лента уже поднялась.
    const promised = this.backIn;
    this.backIn = 0;
    if (promised > 0) {
      this.resuming = true;
      this.cb.onStatus('paused');
      this.later(jitter(Math.max(BACK_EARLY_MS, promised)));
      return;
    }
    // Обещание не сбылось — дальше это обычный отказ, и называть его паузой
    // нельзя: человек ждал бы возврата, которого не будет.
    this.resuming = false;
    this.attempt += 1;
    // Экспоненциальный backoff с потолком: при падении сервера сотня клиентов
    // не должна добивать его переподключениями.
    const delay = Math.min(30_000, 500 * 2 ** Math.min(this.attempt, 6));
    this.cb.onStatus('reconnecting');
    this.later(jitter(delay));
  }

  private later(delay: number): void {
    setTimeout(() => {
      if (!this.closed) this.connect();
    }, delay);
  }

  /** Сокет ещё принимает кадр: рвать его из‑за тишины рано. */
  alive(): boolean {
    const ws = this.ws;
    if (ws === null) return false;
    // Числа, не WebSocket.CLOSING: в тесте конструктор подменяют, константы пропадают.
    return ws.readyState !== 2 && ws.readyState !== 3;
  }

  /** Сокет «live», а кадра нет — не ждать backoff до 30 с. */
  retry(): void {
    if (this.closed) return;
    this.attempt = 0;
    this.reconnect();
  }

  close(): void {
    this.closed = true;
    const ws = this.ws;
    this.ws = null;
    ws?.close();
  }
}
